// frontend/src/components/SiteHeader.js
// Shared marketing-site header (landing page + /tools/<slug> pages).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './UserMenu';
import { TOOL_GROUPS } from '../tools';

const SiteHeader = () => {
  const { authenticated } = useAuth();
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  return (
    <header className="bg-surface-50/90 backdrop-blur-md fixed w-full top-0 z-50 border-b border-surface-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/">
              <img src="/assets/kk-logo-wordmark.png" alt="KarbonKit" className="h-8 w-auto" />
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-8 text-sm">
            <div
              className="relative"
              onMouseEnter={() => setShowToolsMenu(true)}
              onMouseLeave={() => setShowToolsMenu(false)}
            >
              <button className="text-surface-600 hover:text-surface-900 transition-colors flex items-center gap-1">
                Tools
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showToolsMenu && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="bg-white rounded-lg border border-surface-200 shadow-lg py-1 min-w-[240px]">
                    {TOOL_GROUPS.map(({ category, items }) => (
                      <div key={category.id} className="py-1">
                        <p className="px-4 pt-1.5 pb-1 text-xs font-medium text-surface-400">{category.name}</p>
                        {items.map((tool) => (
                          <Link
                            key={tool.slug}
                            to={`/tools/${tool.slug}`}
                            onClick={() => setShowToolsMenu(false)}
                            className="block px-4 py-1.5 text-surface-600 hover:text-surface-900 hover:bg-surface-50 transition-colors"
                          >
                            {tool.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="https://electrifiedhome.org/" target="_blank" rel="noopener noreferrer" className="text-surface-600 hover:text-surface-900 transition-colors">Demo site</a>
            {authenticated && (
              <Link to="/dashboard" className="text-surface-600 hover:text-surface-900 transition-colors">
                Dashboard
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-2 md:space-x-4">
            <UserMenu />
            <button
              onClick={() => setShowMobileMenu((v) => !v)}
              className="md:hidden p-2 -mr-2 text-surface-700 hover:text-surface-900 transition-colors"
              aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-surface-200 py-3 space-y-1">
            <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-surface-400">Tools</p>
            {TOOL_GROUPS.map(({ category, items }) => (
              <div key={category.id} className="pb-1">
                <p className="px-3 pt-1 text-xs text-surface-400">{category.name}</p>
                {items.map((tool) => (
                  <Link
                    key={tool.slug}
                    to={`/tools/${tool.slug}`}
                    onClick={() => setShowMobileMenu(false)}
                    className="block px-3 py-2 rounded-md text-sm text-surface-700 hover:bg-surface-100"
                  >
                    {tool.name}
                  </Link>
                ))}
              </div>
            ))}
            <div className="border-t border-surface-100 my-2" />
            <a href="https://electrifiedhome.org/" target="_blank" rel="noopener noreferrer" onClick={() => setShowMobileMenu(false)} className="block px-3 py-2 rounded-md text-sm text-surface-700 hover:bg-surface-100">Demo site</a>
            <Link to="/docs" onClick={() => setShowMobileMenu(false)} className="block px-3 py-2 rounded-md text-sm text-surface-700 hover:bg-surface-100">Docs</Link>
            <Link to="/principles" onClick={() => setShowMobileMenu(false)} className="block px-3 py-2 rounded-md text-sm text-surface-700 hover:bg-surface-100">How we work</Link>
            {authenticated && (
              <Link to="/dashboard" onClick={() => setShowMobileMenu(false)} className="block px-3 py-2 rounded-md text-sm text-surface-700 hover:bg-surface-100">Dashboard</Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default SiteHeader;
